import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { isDefined } from '@fi-shared/utils/is-defined';
import { traverseDom } from '@fi-shared/utils/traverse-dom';

@Injectable()
export class HtmlPreviewService {
    private readonly previewHtmlAttributes: ReadonlyArray<string> = [
        'font-family',
        'font-style',
        'font-weight',
        'font-size',
        'color',
        'text-decoration',
        'line-height',
        'margin',
        'padding',
        'max-width',
        'list-style',
        'background-color',
        'border-radius',
        'display',
        'box-shadow',
        'box-sizing',
        'word-wrap',
        'white-space',
        'overflow',
        'text-align',
        'position',
        'min-height',
        'webkit-flex',
        'width',
        'height',
        'flex',
        'flex-basis',
        'flex-direction',
        'flex-flow',
        'flex-grow',
        'flex-shrink',
        'flex-wrap',
        'webkit-box-flex',
        'webkit-box-basis',
        'webkit-box-direction',
        'webkit-box-flow',
        'webkit-box-grow',
        'webkit-box-shrink',
        'webkit-box-wrap',
        'top',
        'bottom',
        'right',
        'left',
        'float',
        'list-style-type',
    ];
    private readonly tablePreviewHtmlAttributes: ReadonlyArray<string> = [
        'table-layout',
        'border-collapse',
        'border-spacing',
        'empty-cells',
        'border',
        'border-top',
        'border-right',
        'border-bottom',
        'border-left',
        'border-style',
        'border-width',
        'border-color',
        'border-top-style',
        'border-top-width',
        'border-top-color',
        'border-right-style',
        'border-right-width',
        'border-right-color',
        'border-bottom-style',
        'border-bottom-width',
        'border-bottom-color',
        'border-left-style',
        'border-left-width',
        'border-left-color',
    ];
    // special cases
    private readonly hideElementsQuerySelectors: ReadonlyArray<string> = [
        '.mat-button-focus-overlay',
        '.mat-radio-persistent-ripple',
        '.mat-radio-input.cdk-visually-hidden',
        '.mat-form-field-outline',
        '.mat-form-field-label-wrapper',
        '.mat-form-field-hint-wrapper',
        '.mat-form-field-label-wrapper',
        '.mat-form-field-subscript-wrapper',
        '.mat-select-arrow-wrapper',
        '.exclude-from-print-html',
        '.fi-text-item-table-panel-handler',
        '.fi-text-item-media-panel-handler',
        '.fi-ai-assistance-panel-handler',
        '.fi-dnd-overlay',
    ];
    private renderer: Renderer2;

    constructor(rendererFactory: RendererFactory2) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    public dataURItoBlob(dataURI, mimeString) {
        let byteString: string;
        if (dataURI.split(',')[0].indexOf('base64') >= 0) byteString = atob(dataURI.split(',')[1]);
        else byteString = decodeURIComponent(dataURI.split(',')[1]);

        const ia = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }

        return new Blob([ia], { type: mimeString });
    }

    getPreviewHtml(element: Element, previewWidth?, postProcessCallback?) {
        if (!isDefined(previewWidth)) {
            previewWidth = '800px';
        }

        const layout = element as HTMLElement;
        const originalStyle = layout.getAttribute('style');
        const hadPreviewGeneratingClass = layout.classList.contains('fi-preview-generating');
        let result = '';

        // The live root is temporarily resized for computed styles, so always restore it after preview generation.
        try {
            this.renderer.addClass(layout, 'fi-preview-generating');
            this.renderer.setStyle(layout, 'transform', '');
            this.renderer.setStyle(layout, 'width', previewWidth);

            const previewLayout = layout.cloneNode(true) as HTMLElement;
            const previewNodes: HTMLElement[] = [];
            let previewNodeIndex = 0;

            traverseDom(previewLayout, (node) => {
                previewNodes.push(node);
            });

            traverseDom(layout, (node) => {
                const previewNode = previewNodes[previewNodeIndex++];
                const cs = window.getComputedStyle(node);
                const tagName = node.tagName.toLowerCase();

                // fix img width 0px
                const isImgWithoutWidth = tagName === 'img' && cs.getPropertyValue('width') === '0px';

                let styleStr = '';
                const previewHtmlAttributes = this.isTableElement(tagName)
                    ? [...this.previewHtmlAttributes, ...this.tablePreviewHtmlAttributes]
                    : this.previewHtmlAttributes;

                for (let i = 0; i < previewHtmlAttributes.length; i++) {
                    const key = previewHtmlAttributes[i];
                    let value = cs.getPropertyValue(key);
                    if (tagName === 'iframe' && key === 'display') {
                        // https://chainformation.atlassian.net/browse/FI-3705
                        // don't show iframes in preview
                        value = 'none';
                    }

                    if (tagName !== 'input' && key === 'display' && value === 'inline-block') {
                        value = 'block';
                        //styleStr += 'page-break-inside:avoid;';
                        //styleStr += 'page-break-before:auto;';
                    }

                    if (isImgWithoutWidth) {
                        if (key === 'width') {
                            value = '100%';
                        } else if (key === 'margin') {
                            value = 'null';
                        }
                    }

                    if (key === 'height' && !this.isTableLayoutElement(tagName)) {
                        value = null;
                    }
                    if (key === 'min-height') {
                        value = cs.getPropertyValue('height');
                    }
                    //};
                    if (value) {
                        styleStr += key + ':' + value + ';';
                    }
                }
                styleStr += this.getPdfTableBorderStyles(tagName, cs);
                this.renderer.setAttribute(previewNode, 'style', styleStr);
            });

            //const elementsToRemove: HTMLElement[] = [];
            previewLayout.querySelectorAll(this.hideElementsQuerySelectors.join(', ')).forEach((el) => {
                this.renderer.setStyle(el, 'display', 'none');
            });
            /*element.querySelectorAll('textarea.mat-input-element').forEach(el => {
                const text = this.renderer.createText(el.value);
                elementsToRemove.push(text);
                this.renderer.appendChild(el, text)
            })
            element.querySelectorAll('input.mat-input-element').forEach(el => {
                const text = this.renderer.createText(el.value);
                elementsToRemove.push(text);
                this.renderer.appendChild(el.parentElement, text);
            })*/

            this.renderer.setStyle(previewLayout, 'margin', '0px');
            this.renderer.setStyle(previewLayout, 'padding', '0px');
            if (postProcessCallback) postProcessCallback(previewLayout);
            result = previewLayout.outerHTML;
        } finally {
            if (originalStyle !== null) {
                this.renderer.setAttribute(layout, 'style', originalStyle);
            } else {
                this.renderer.removeAttribute(layout, 'style');
            }
            if (!hadPreviewGeneratingClass) {
                this.renderer.removeClass(layout, 'fi-preview-generating');
            }
        }
        // #706 Print functionality Documents. Add display: -webkit-flex;
        result = result.replace(/(display:\s*flex;)/g, 'display:-webkit-box;');
        result = result.replace(/(;|\")\s*flex:(.+?;)/g, '$1-webkit-flex:$2');
        result = result.replace(/(;|\")\s*flex-(.+?:.+?;)/g, '$1-webkit-flex-$2');

        //elementsToRemove.forEach(el => el.remove());
        return result;
    }

    private isTableLayoutElement(tagName: string): boolean {
        return ['table', 'td', 'th'].includes(tagName);
    }

    private isTableElement(tagName: string): boolean {
        return ['table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th'].includes(tagName);
    }

    private getPdfTableBorderStyles(tagName: string, computedStyle: CSSStyleDeclaration): string {
        if (!this.isTableLayoutElement(tagName)) {
            return '';
        }

        return ['top', 'right', 'bottom', 'left']
            .map((side) => this.getPdfTableBorderSideStyle(side, computedStyle))
            .join('');
    }

    private getPdfTableBorderSideStyle(side: string, computedStyle: CSSStyleDeclaration): string {
        const style = computedStyle.getPropertyValue(`border-${side}-style`);
        const width = computedStyle.getPropertyValue(`border-${side}-width`);
        const color = computedStyle.getPropertyValue(`border-${side}-color`);

        if (!style || style === 'none' || style === 'hidden' || parseFloat(width) === 0) {
            return '';
        }

        return `border-${side}:1pt ${style} ${color};`;
    }
    //
    // getPreviewImage(element, onCanvasReady) {
    //     const previewWidth = 600;
    //     const layout = $(element);
    //     const style = layout.attr('style') || '';
    //     layout.css('transform', '');
    //     layout.css('width', previewWidth + 'px');
    //     layout.css('max-width', previewWidth + 'px');
    //
    //     const promise = new Promise<Blob>((resolve, reject) => {
    //         window.html2canvas($('.start-page-layout').get(0), {
    //             height: previewWidth,
    //             width: previewWidth,
    //             useCORS: true
    //         }).then((canvas) => {
    //             if (onCanvasReady) {
    //                 onCanvasReady();
    //             }
    //
    //             const mimeString = "image/png";
    //             resolve(canvas ? this.dataURItoBlob(canvas.toDataURL(mimeString), mimeString) : null);
    //         });
    //
    //         layout.attr('style', style);
    //     });
    //
    //     return promise;
    // }
}
